<script setup lang="ts">
import CssLogo from "@/assets/icons/CssLogo.svg";
import jsLogo from "@/assets/icons/JsLogo.svg";
import tsLogo from "@/assets/icons/TsLogo.svg";
import vueLogo from "@/assets/icons/VueLogo.svg";
import dashboard from "@/assets/icons/dashboard.svg";
import groups from "@/assets/icons/groups.svg";
import workspacePremium from "@/assets/icons/workspace_premium.svg";
import courseInfoImage from "@/assets/images/CourseInfoImage.png";
import InfoCardComp from "@/components/apply-page/InfoCardComp.vue";
import cardsStar from "@/assets/icons/cards_star.svg";
import globeIcon from "@/assets/icons/globeIcon.svg";
import loyalty from "@/assets/icons/loyalty.svg";

const skills = [
	{
		title: "Hands-on learning",
		description:
			"Learn by building real projects — not just watching slides. Every concept goes straight into practice.",
		icon: dashboard,
		variant: "light",
	},
	{
		title: "Teamwork experience",
		description:
			"Collaborate with others the way real development teams do, and learn how great software gets shipped together.",
		icon: groups,
		variant: "light",
	},
	{
		title: "A portfolio project",
		description:
			"Finish with a complete project you can proudly showcase in your CV or portfolio.",
		icon: workspacePremium,
		variant: "light",
	},
];

const topics = [
	{
		title: "JavaScript",
		description:
			"The language of the web — logic, interactivity and the foundation of everything.",
		icon: jsLogo,
		variant: "dark",
	},
	{
		title: "TypeScript",
		description:
			"Typed, scalable JavaScript that catches bugs early and powers larger apps.",
		icon: tsLogo,
		variant: "dark",
	},
	{
		title: "Vue",
		description:
			"A reactive framework for building modern, component-based user interfaces.",
		icon: vueLogo,
		variant: "dark",
	},
	{
		title: "CSS3",
		description:
			"Styling and layout — turning structure into polished, responsive design.",
		icon: CssLogo,
		variant: "dark",
	},
];
</script>

<template>
	<section class="course-info">
		<!-- HERO -->
		<section class="hero-section">
			<div class="hero-wrapper">
				<div class="hero-copy">
					<ul class="hero-bullets">
						<li>
							<img :src="cardsStar" alt="" />
							<span>8 lectures</span>
						</li>

						<li>
							<img :src="globeIcon" alt="" />
							<span>100% online</span>
						</li>

						<li>
							<img :src="loyalty" alt="" />
							<span>Completely free</span>
						</li>
					</ul>

					<h1>Frontend Development Course</h1>

					<p class="hero-subtitle">Level up your knowledge — for free.</p>

					<p class="hero-text">
						ENON Solutions is organizing a free 8-lecture online course for
						aspiring frontend developers. Learn from experienced programmers who
						guide you, answer your questions, and help you build practical
						skills.
					</p>

					<div class="hero-actions">
						<a href="/apply" class="btn btn-primary"> Apply here </a>

						<a href="#see-what-will-learn" class="btn btn-outline">
							See what you'll learn
						</a>
					</div>
				</div>

				<div class="hero-visual">
					<img
						:src="courseInfoImage"
						alt="Frontend Development Course"
						class="hero-image"
					/>
				</div>
			</div>
		</section>

		<!-- REAL SKILLS -->
		<section class="skills-section">
			<div class="skills-wrapper">
				<h2>Real skills, real projects, real proof</h2>

				<div class="skills-grid">
					<InfoCardComp
						v-for="skill in skills"
						:key="skill.title"
						:title="skill.title"
						:description="skill.description"
						:variant="skill.variant"
					>
						<template #icon>
							<img :src="skill.icon" alt="" />
						</template>
					</InfoCardComp>
				</div>
			</div>
		</section>

		<!-- FOUR CORE TOPICS -->
		<!-- FOUR CORE TOPICS -->
		<section id="see-what-will-learn" class="topics-section">
			<div class="topics-wrapper">
				<h2>Four core frontend topics</h2>

				<div class="topics-grid">
					<InfoCardComp
						v-for="topic in topics"
						:key="topic.title"
						:title="topic.title"
						:description="topic.description"
						:variant="topic.variant"
					>
						<template #icon>
							<img :src="topic.icon" alt="" />
						</template>
					</InfoCardComp>
				</div>
			</div>
		</section>

		<!-- BOTTOM SECTION -->
		<section class="bottom-section">
			<div class="bottom-wrapper">
				<div class="bottom-glow-wrapper">
					<div class="bottom-glow"></div>

					<h2>Ready to start building?</h2>
				</div>

				<p>
					Join the free 8-lecture online course for aspiring frontend developers
					and walk away with practical skills and a project for your portfolio.
				</p>

				<a href="/apply" class="btn btn-primary"> Apply here </a>
			</div>
		</section>
	</section>
</template>

<style scoped lang="scss">
@use "@/assets/css/vars" as *;

.course-info {
	background: var(--es-background-color);
	color: $text-white;
	overflow: hidden;
}

/* ===========================================================
   HERO
=========================================================== */

.hero-section {
	background: var(--es-background-color);
}

.hero-wrapper {
	max-width: $content-max-width;
	width: 100%;
	margin: 0 auto;
	padding: 180px 24px 120px;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	gap: 64px;
}

.hero-copy {
	flex: 1;
	max-width: 760px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 28px;
	animation: appear1 0.6s ease both;
}

.hero-copy h1 {
	margin: 0;
	font-family: $font-heading;
	font-size: clamp(40px, 5vw, 64px);
	font-weight: $font-bold;
	line-height: 1.05;
	letter-spacing: -1.5px;
}

.hero-subtitle {
	margin: 0;
	color: $green-text;
	font-family: $font-heading;
	font-size: clamp(20px, 2vw, 24px);
}

.hero-text {
	margin: 0;
	color: #aab2bd;
	font-size: 17px;
	line-height: 1.7;
}

.hero-bullets {
	display: flex;
	flex-wrap: wrap;
	gap: 24px;
	margin: 0;
	padding: 0;
	list-style: none;
}

.hero-bullets li {
	display: flex;
	align-items: center;
	gap: 10px;
}

.hero-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
}

.hero-visual {
	flex: 1;
	max-width: 640px;
	animation: appear2 0.6s ease both;
}

.hero-image {
	width: 100%;
	display: block;
}

/* ===========================================================
   BUTTONS
=========================================================== */

.btn {
	text-decoration: none;
	transition: 0.25s;
}

.btn:hover {
	transform: translateY(-2px);
}

.btn-primary {
	display: flex;
	align-items: center;
	justify-content: center;
	height: $button-height;
	padding: 0 28px;
	border-radius: $button-border-radius;
	background: $app-color;
	color: $text-white;
}

.btn-outline {
	display: flex;
	align-items: center;
	justify-content: center;
	height: $button-height;
	padding: 0 28px;
	border-radius: $button-border-radius;
	border: 1px solid #5b6573;
	background: transparent;
	color: white;
}

/* ===========================================================
   SKILLS
=========================================================== */

.skills-section {
	padding: 180px 24px;
	background: $section-back-alt;
	background-image: url("@/assets/images/WhiteBackground.png");
	background-size: cover;
	background-position: center;
}

.skills-wrapper {
	max-width: $content-max-width;
	margin: 0 auto;
}

.skills-wrapper h2 {
	margin: 0 0 48px;
	text-align: center;
	color: $second-app-color;
	font-family: $font-heading;
	font-size: clamp(30px, 4vw, 42px);
	font-weight: $font-bold;
}

.skills-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 24px;
}

/* ===========================================================
   TOPICS
=========================================================== */

.topics-section {
	padding: 180px 24px;
	scroll-margin-top: 100px;
}

.topics-wrapper {
	max-width: $content-max-width;
	margin: 0 auto;
}

.topics-wrapper h2 {
	margin: 0 0 48px;
	text-align: center;
	color: $app-color;
	font-family: $font-heading;
	font-size: clamp(30px, 4vw, 42px);
	font-weight: $font-bold;
}

.topics-grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 24px;
}

/* ===========================================================
   BOTTOM CTA
=========================================================== */

.bottom-section {
	padding: 180px 24px;
	background: var(--es-background-color);
	text-align: center;
}

.bottom-wrapper {
	max-width: 900px;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;
}

.bottom-glow-wrapper {
	position: relative;
	display: inline-flex;
	justify-content: center;
	align-items: center;
}

.bottom-glow {
	position: absolute;
	width: 500px;
	height: 500px;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	border-radius: 50%;
	background: radial-gradient(circle, #587da8 0%, rgba(87, 125, 168, 0) 43%);
	filter: blur(80px);
	z-index: 0;
}

.bottom-wrapper h2 {
	margin: 0;
	font-family: $font-heading;
	font-size: clamp(34px, 4vw, 46px);
	font-weight: $font-bold;
}

.bottom-wrapper p {
	margin: 0;
	max-width: 640px;
	color: #aab2bd;
	font-size: 17px;
	line-height: 1.7;
}

.bottom-wrapper .btn-primary {
	background: $green-text;
	color: white;
}

/* ===========================================================
   RESPONSIVE
=========================================================== */

@media (max-width: 1100px) {
	.hero-wrapper {
		flex-wrap: wrap;
		padding: 120px 20px 88px;
		gap: 48px;
	}

	.skills-section,
	.topics-section,
	.bottom-section {
		padding: 88px 20px;
	}

	.topics-grid {
		grid-template-columns: repeat(2, 1fr);
	}
}

@media (max-width: 768px) {
	.hero-wrapper {
		flex-direction: column;
		align-items: stretch;
		padding: 96px 16px 64px;
		gap: 48px;
	}

	.hero-copy {
		max-width: 100%;
	}

	.hero-actions {
		width: 100%;
	}

	.hero-actions .btn {
		width: 100%;
	}

	.skills-section,
	.topics-section,
	.bottom-section {
		padding: 64px 16px;
	}

	.skills-grid,
	.topics-grid {
		grid-template-columns: 1fr;
	}

	.skills-wrapper h2,
	.topics-wrapper h2 {
		margin-bottom: 40px;
	}

	.bottom-glow {
		width: 320px;
		height: 320px;
	}
}

@media (max-width: 480px) {
	.hero-copy h1 {
		font-size: 36px;
	}

	.hero-bullets {
		flex-direction: column;
		align-items: flex-start;
	}
}
</style>
